import { api } from "./client";

export type MediaType = "image" | "video" | "audio";
export type UploadStatus = "pending" | "processing" | "ready" | "failed";
export type UploadCategory =
  | "avatar"
  | "event"
  | "provider"
  | "post"
  | "general"
  | "nostalgia";

export interface UploadResult {
  id: string;
  url: string;
  thumbnailUrl?: string;
  width: number;
  height: number;
  size: number;
  mimeType: string;
  mediaType?: MediaType;
  duration?: number;
  status?: UploadStatus;
  /** Server-derived: in_app_camera | file_picker | unknown (open string). */
  captureSource?: string;
  processError?: string;
}

interface ApiResponse<T> {
  success: boolean;
  data: T;
}

// Size limits in bytes (must match backend)
export const MEDIA_SIZE_LIMITS = {
  image: 10 * 1024 * 1024, // 10MB
  video: 50 * 1024 * 1024, // 50MB
  audio: 20 * 1024 * 1024, // 20MB
} as const;

/** A verification document's limit (must match backend). */
const DOCUMENT_SIZE_LIMIT = 10 * 1024 * 1024;

// Allowed MIME types
export const ALLOWED_IMAGE_TYPES = [
  "image/jpeg",
  "image/png",
  "image/webp",
  "image/gif",
];

export const ALLOWED_VIDEO_TYPES = ["video/mp4", "video/webm", "video/quicktime"];

export const ALLOWED_AUDIO_TYPES = [
  "audio/mpeg",
  "audio/mp3",
  "audio/wav",
  "audio/aac",
  "audio/ogg",
  "audio/x-m4a",
  "audio/mp4",
];

/**
 * Upload an image file
 */
export async function uploadImage(
  file: File,
  category: UploadCategory = "general",
): Promise<UploadResult> {
  // Client-side validation
  if (!ALLOWED_IMAGE_TYPES.includes(file.type)) {
    throw new Error("Only JPEG, PNG, WebP, and GIF images are allowed");
  }
  if (file.size > MEDIA_SIZE_LIMITS.image) {
    throw new Error("Image file must be less than 10MB");
  }

  const response = await api.uploadFile<ApiResponse<UploadResult>>(
    "/upload/image",
    file,
    { category },
  );
  return response.data;
}

/**
 * Camera-attested image upload. THE ROUTE is the attestation: the server
 * records captureSource = in_app_camera because the bytes came through this
 * door, never because a field claimed it. In the real app only the in-app
 * camera calls this; the Lab calls it to exercise the attestation end to end.
 */
export async function uploadCameraImage(
  file: File,
  category: UploadCategory = "post",
): Promise<UploadResult> {
  if (!ALLOWED_IMAGE_TYPES.includes(file.type)) {
    throw new Error("Only JPEG, PNG, WebP, and GIF images are allowed");
  }
  if (file.size > MEDIA_SIZE_LIMITS.image) {
    throw new Error("Image file must be less than 10MB");
  }
  const response = await api.uploadFile<ApiResponse<UploadResult>>(
    "/upload/camera/image",
    file,
    { category },
  );
  return response.data;
}

/** Camera-attested video upload — see uploadCameraImage. */
export async function uploadCameraVideo(
  file: File,
  category: UploadCategory = "post",
): Promise<UploadResult> {
  if (!ALLOWED_VIDEO_TYPES.includes(file.type)) {
    throw new Error("Only MP4, WebM, and QuickTime videos are allowed");
  }
  if (file.size > MEDIA_SIZE_LIMITS.video) {
    throw new Error("Video file must be less than 50MB");
  }
  const response = await api.uploadFile<ApiResponse<UploadResult>>(
    "/upload/camera/video",
    file,
    { category },
  );
  return response.data;
}

/**
 * Upload a video file
 */
export async function uploadVideo(
  file: File,
  category: UploadCategory = "general",
): Promise<UploadResult> {
  // Client-side validation
  if (!ALLOWED_VIDEO_TYPES.includes(file.type)) {
    throw new Error("Only MP4, WebM, and QuickTime videos are allowed");
  }
  if (file.size > MEDIA_SIZE_LIMITS.video) {
    throw new Error("Video file must be less than 50MB");
  }

  const response = await api.uploadFile<ApiResponse<UploadResult>>(
    "/upload/video",
    file,
    { category },
  );
  return response.data;
}

/**
 * Upload an audio file
 */
export async function uploadAudio(
  file: File,
  category: UploadCategory = "general",
): Promise<UploadResult> {
  // Client-side validation
  if (!ALLOWED_AUDIO_TYPES.includes(file.type)) {
    throw new Error("Only MP3, WAV, AAC, and OGG audio files are allowed");
  }
  if (file.size > MEDIA_SIZE_LIMITS.audio) {
    throw new Error("Audio file must be less than 20MB");
  }

  const response = await api.uploadFile<ApiResponse<UploadResult>>(
    "/upload/audio",
    file,
    { category },
  );
  return response.data;
}

// ── Verification documents: private, opened only with a token ─────────────

export const ALLOWED_DOCUMENT_TYPES = ["application/pdf", "image/jpeg", "image/png"];

export interface UploadedDocument {
  id: string;
  /** The address to send with a verification. It opens only with a token. */
  url: string;
  fileName: string;
  mimeType: string;
  size: number;
}

/** A licence, an ID or a certificate. Stored privately; never a public URL. */
export async function uploadDocument(file: File): Promise<UploadedDocument> {
  if (!ALLOWED_DOCUMENT_TYPES.includes(file.type)) {
    throw new Error("Only PDF, JPEG and PNG documents are allowed");
  }
  if (file.size > DOCUMENT_SIZE_LIMIT) {
    throw new Error("A document must be less than 10MB");
  }
  const response = await api.uploadFile<ApiResponse<UploadedDocument>>("/upload/document", file);
  return response.data;
}

/** The upload id inside a document address (…/v1/upload/{id}/file), or null. */
export function documentIdOf(url: string): string | null {
  try {
    return new URL(url).pathname.match(/\/v1\/upload\/([0-9a-f-]{36})\/file$/i)?.[1] ?? null;
  } catch {
    return null;
  }
}

/**
 * Open a document in a new tab. Its address needs a token, which a plain link
 * does not send, so the file is fetched with one and shown from a local copy.
 */
export async function openDocument(url: string): Promise<void> {
  const id = documentIdOf(url);
  if (!id) {
    window.open(url, "_blank", "noopener");
    return;
  }
  const blob = await api.getBlob(`/upload/${id}/file`);
  const local = URL.createObjectURL(blob);
  window.open(local, "_blank", "noopener");
  setTimeout(() => URL.revokeObjectURL(local), 60_000);
}

/**
 * Get upload processing status
 */
export async function getUploadStatus(uploadId: string): Promise<UploadResult> {
  const response = await api.get<ApiResponse<UploadResult>>(
    `/upload/${uploadId}/status`,
  );
  return response.data;
}

/**
 * Determine media type from file
 */
export function getMediaType(file: File): MediaType | null {
  if (ALLOWED_IMAGE_TYPES.includes(file.type)) return "image";
  if (ALLOWED_VIDEO_TYPES.includes(file.type)) return "video";
  if (ALLOWED_AUDIO_TYPES.includes(file.type)) return "audio";
  return null;
}

/**
 * Get allowed file types for a media type
 */
export function getAllowedTypes(mediaType: MediaType): string[] {
  switch (mediaType) {
    case "image":
      return ALLOWED_IMAGE_TYPES;
    case "video":
      return ALLOWED_VIDEO_TYPES;
    case "audio":
      return ALLOWED_AUDIO_TYPES;
  }
}

/**
 * Get size limit for a media type
 */
export function getSizeLimit(mediaType: MediaType): number {
  return MEDIA_SIZE_LIMITS[mediaType];
}

/**
 * Format file size for display
 */
export function formatFileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}
